import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import type { Unit } from '../../types';
import type { UnitTooltipContent, SmClusterTooltipContent, LgClusterTooltipContent } from '../../hooks/useMobileDrawer';
import { useFilters } from '@/contexts/FilterContext';
import { UnitPreviewCard as UnitPreviewCard } from '@/components/unit/UnitPreviewCard';
import { ClusterContent } from './shared/ClusterContent';
import { SmallClusterContent } from './shared/SmallClusterContent';

interface MobileInfoDrawerContentProps {
  content: UnitTooltipContent | SmClusterTooltipContent | LgClusterTooltipContent;
  allUnits?: Unit[]; // All units for zoom calculation
  onClose: () => void;
  mapRef?: React.RefObject<mapboxgl.Map | null>;
}

// Helper to generate semantic slug
const generateSlug = (unit: Unit) => {
  return unit.unit_code;
};

export function MobileInfoDrawerContent({ content, allUnits, onClose, mapRef }: MobileInfoDrawerContentProps) {
  const navigate = useNavigate();
  const {
    setSelectedCompanyIds,
    setSelectedGroupIds,
    setSelectedIndustryTypes,
    setSelectedModels,
    saveFiltersForRestore,
    restoreSavedFilters,
    hasSavedFilters,
    setShouldExpandAll
  } = useFilters();

  if (content.type === 'unit_tooltip') {
    return <UnitTooltipRenderer unit={content.unit} onClose={onClose} />;
  } else if (content.type === 'sm_cluster_tooltip') {
    return (
      <SmallClusterContent
        units={content.units}
        initialActiveUnit={content.initialActiveUnit}
        onClose={onClose}
        navigate={(path) => navigate(path)}
        isMobile={true}
      />
    );
  } else {
    return (
      <ClusterContent
        units={content.units}
        allUnits={allUnits}
        onClose={onClose}
        map={mapRef?.current || null}
        isMobile={true}
        filterActions={{
          setSelectedCompanyIds,
          setSelectedGroupIds,
          setSelectedIndustryTypes,
          setSelectedModels,
          saveFiltersForRestore,
          restoreSavedFilters,
          setShouldExpandAll
        }}
        hasSavedFilters={hasSavedFilters}
      />
    );
  }
}

// Unit Tooltip (Single Unit) - matches UnitHoverCard styling
function UnitTooltipRenderer({ unit, onClose: _onClose }: { unit: Unit; onClose: () => void }) {
  const navigate = useNavigate();
  const isMobile = true;

  const handleAction = (action: 'view' | 'solar' | 'charger' | 'hvac' | 'starlink') => {
    const slug = generateSlug(unit);
    const tabParam = action !== 'view' ? `&tab=${action}` : '';
    // Navigate to detail view - URL state sync in Map.tsx handles drawer state
    // Removing onClose() fixes race condition where drawer reopens during close animation
    navigate(`/map?unit=${slug}&view=detail${tabParam}`);
  };

  return (
    <UnitPreviewCard
      unit={unit}
      isMobile={isMobile}
      onAction={handleAction}
      imageHeight="h-40"
      className="h-full flex flex-col cursor-pointer"
    />
  );
}
